import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import type { ConsolidatedEventCluster, SessionEvent, UserAssertion } from '@/types';
import { formatVideoTime } from '@/types';
import DecisionEventEditPopover, {
  type DecisionEventEditValues,
} from '@/components/review/DecisionEventEditPopover';
import VideoTimestampPreviewDialog from '@/components/review/VideoTimestampPreviewDialog';
import ConsensusComparisonMobileList from '@/components/review/ConsensusComparisonMobileList';
import ConsensusRateInfoIcon from '@/components/review/ConsensusRateInfoIcon';
import {
  ConsensusOpinionCell,
  eventOnFieldLabel,
} from '@/components/review/consensusComparisonCells';
import {
  agreementMetricColor,
  assertionLinkedEventId,
  clusterHasOfficialAnswer,
  formatClusterAgreementDisplayLabel,
  getClusterAssertionForUser,
  getClusterOfficialAssertions,
  getClusterOwnerAssertion,
  isAssertionAlignedWithOfficials,
  isParticipantAlignedWithOwner,
} from '@/components/review/consensus';
import {
  officialColumnHeadSurface,
  statusTextColor,
  stickyColumnShadow,
  tableHeadSurface,
} from '@/utils/themeSurfaces';
import {
  REVIEW_EDIT_POPOVER_Z,
  reviewEditDialogZ,
  reviewEditMenuZ,
} from '@/components/review/reviewZIndex';

interface Props {
  clusters: ConsolidatedEventCluster[];
  events?: SessionEvent[];
  canEditEvents?: boolean;
  onEditEvent?: (eventId: string, values: DecisionEventEditValues) => void | Promise<void>;
  onJumpToTime?: (seconds: number) => void;
  videoId?: string | null;
  participantView?: boolean;
  currentUserId?: number;
  officialUserIds?: number[];
  resolveOfficialLabel?: (userId: number) => string;
  videoDuration?: number;
  onPreviewTime?: (seconds: number) => void;
  popoverZIndex?: number;
  menuZIndex?: number;
}

interface ParticipantColumn {
  userId: string;
  label: string;
}

interface OfficialColumn {
  userId: string;
  label: string;
}

function buildOfficialColumns(
  officialUserIds: number[] | undefined,
  resolveLabel: (userId: number) => string
): OfficialColumn[] {
  if (!officialUserIds?.length) {
    return [{ userId: '', label: '官方' }];
  }
  return officialUserIds.map((id) => ({
    userId: String(id),
    label: `官方 (${resolveLabel(id)})`,
  }));
}

const MAX_VISIBLE_COLS = 8;
const COL_MIN_WIDTH = 132;
const FIRST_COL_MIN_WIDTH = 248;
/** Body sticky col < header row < header corner (top-left) */
const Z_STICKY_BODY_COL = 1;
const Z_STICKY_HEADER = 5;
const Z_STICKY_HEADER_CORNER = 6;

function buildParticipantColumns(
  clusters: ConsolidatedEventCluster[],
  participantView: boolean,
  currentUserId?: number
): ParticipantColumn[] {
  const map = new Map<string, string>();
  for (const cluster of clusters) {
    for (const a of cluster.assertions) {
      if (a.role !== 'participant') continue;
      if (participantView && currentUserId != null && a.userId !== String(currentUserId)) continue;
      if (!map.has(a.userId)) map.set(a.userId, a.userName);
    }
  }
  return [...map.entries()]
    .map(([userId, label]) => ({
      userId,
      label: participantView ? '我的判決' : label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'zh-HK'));
}

function findParticipantAssertion(
  cluster: ConsolidatedEventCluster,
  userId: string
): UserAssertion | null {
  return cluster.assertions.find((a) => a.role === 'participant' && a.userId === userId) ?? null;
}

function cellHighlightSx(aligned: boolean | null, isOfficial: boolean) {
  if (isOfficial || aligned == null) return {};
  if (aligned) {
    return { bgcolor: 'rgba(46,125,50,0.07)' };
  }
  return {
    bgcolor: 'rgba(198,40,40,0.10)',
    boxShadow: (t: { palette: { error: { main: string } } }) => `inset 3px 0 0 ${t.palette.error.main}`,
  };
}

export default function ConsensusComparisonTable({
  clusters,
  events = [],
  canEditEvents = false,
  onEditEvent,
  onJumpToTime,
  videoId,
  participantView = false,
  currentUserId,
  officialUserIds,
  resolveOfficialLabel = (id) => `用戶 ${id}`,
  videoDuration,
  onPreviewTime,
  popoverZIndex = REVIEW_EDIT_POPOVER_Z,
  menuZIndex,
}: Props) {
  const resolvedMenuZ = menuZIndex ?? reviewEditMenuZ(popoverZIndex);
  const resolvedDialogZ = reviewEditDialogZ(popoverZIndex);
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const headBg = tableHeadSurface(theme);
  const [editing, setEditing] = useState<{ event: SessionEvent; anchorEl: HTMLElement } | null>(null);
  const [videoPreview, setVideoPreview] = useState<{
    seconds: number;
    subtitle: string;
  } | null>(null);

  const openVideoPreview = (cluster: ConsolidatedEventCluster, onFieldLabel: string) => {
    const seconds = Math.max(0, cluster.anchorTimestamp - 0.8);
    if (videoId) {
      setVideoPreview({
        seconds,
        subtitle: `${formatVideoTime(cluster.anchorTimestamp)} · ${onFieldLabel}`,
      });
      return;
    }
    onJumpToTime?.(seconds);
  };
  const eventMap = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const participantColumns = buildParticipantColumns(clusters, participantView, currentUserId);
  const officialColumns = buildOfficialColumns(officialUserIds, resolveOfficialLabel);

  const canEditAssertion = (assertion: UserAssertion | null) => {
    if (!canEditEvents || !assertion) return false;
    const eventId = assertionLinkedEventId(assertion);
    if (!eventId || !eventMap.has(eventId)) return false;
    if (participantView && currentUserId != null) {
      return assertion.userId === String(currentUserId);
    }
    return true;
  };

  const openEdit = (e: React.MouseEvent<HTMLButtonElement>, assertion: UserAssertion) => {
    const eventId = assertionLinkedEventId(assertion);
    if (!eventId) return;
    const event = eventMap.get(eventId);
    if (!event) return;
    const anchor =
      (e.currentTarget as HTMLElement).closest('td') ??
      (e.currentTarget as HTMLElement);
    setEditing({ event, anchorEl: anchor as HTMLElement });
  };
  const participantCount = participantColumns.length;
  const colCount = 1 + officialColumns.length + participantColumns.length;
  const needsHorizontalScroll = colCount > MAX_VISIBLE_COLS;
  const tableMinWidth = FIRST_COL_MIN_WIDTH + Math.max(0, colCount - 1) * COL_MIN_WIDTH;
  const evenColWidth = `${100 / colCount}%`;

  const dataColWidthSx = needsHorizontalScroll
    ? { width: `${100 / colCount}%`, minWidth: COL_MIN_WIDTH }
    : { width: evenColWidth };

  const firstColWidthSx = {
    ...dataColWidthSx,
    minWidth: FIRST_COL_MIN_WIDTH,
    width: needsHorizontalScroll ? `${100 / colCount}%` : evenColWidth,
  };

  const stickyFirstColSx = {
    position: 'sticky' as const,
    left: 0,
    zIndex: Z_STICKY_BODY_COL,
    bgcolor: 'background.paper',
    boxShadow: stickyColumnShadow(theme),
  };

  const stickyFirstHeaderSx = {
    position: 'sticky' as const,
    left: 0,
    top: 0,
    zIndex: Z_STICKY_HEADER_CORNER,
    bgcolor: headBg,
    backgroundColor: headBg,
    boxShadow: stickyColumnShadow(theme),
  };

  const stickyHeaderCellSx = {
    position: 'sticky' as const,
    top: 0,
    zIndex: Z_STICKY_HEADER,
    bgcolor: headBg,
    backgroundColor: headBg,
  };

  const officialHeadBg = officialColumnHeadSurface(theme);

  if (!clusters.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        尚無可比對的共識事件
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <DecisionEventEditPopover
        key={editing?.event.id ?? 'closed'}
        open={!!editing}
        anchorEl={editing?.anchorEl ?? null}
        initial={editing?.event}
        showVideoTime={canEditEvents}
        videoDuration={videoDuration}
        onPreviewTime={onPreviewTime}
        popoverZIndex={popoverZIndex}
        menuZIndex={resolvedMenuZ}
        dialogZIndex={resolvedDialogZ}
        onClose={() => setEditing(null)}
        onSubmit={async (values) => {
          if (editing && onEditEvent) {
            await onEditEvent(editing.event.id, values);
          }
          setEditing(null);
        }}
      />
      <VideoTimestampPreviewDialog
        open={!!videoPreview}
        onClose={() => setVideoPreview(null)}
        videoId={videoId}
        initialSeconds={videoPreview?.seconds ?? 0}
        videoDuration={videoDuration}
        subtitle={videoPreview?.subtitle}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography variant="subtitle1" fontWeight={700}>
              共識比對表
            </Typography>
            {!participantView && <ConsensusRateInfoIcon />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {isMobileLayout
              ? participantView
                ? `點開事件展開查看官方與您的判決 · ${clusters.length} 個事件`
                : `點開事件展開查看官方與各參與者判決 · ${clusters.length} 個事件 · ${participantCount} 位參與者`
              : participantView
                ? `每列為一個事件，欄位為官方與您的判決 · ${clusters.length} 個事件`
                : `每列為一個事件，各欄為參與者在該事件的判決 · ${clusters.length} 個事件 · ${participantCount} 位參與者`}
            {!isMobileLayout && needsHorizontalScroll && !participantView && (
              <>
                {' '}
                · 左右滑動查看更多
              </>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<VerifiedIcon sx={{ fontSize: '14px !important' }} />}
            label="官方"
            variant="outlined"
            sx={{ height: 24 }}
          />
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
            label="一致"
            sx={{ height: 24, bgcolor: 'rgba(46,125,50,0.08)', color: statusTextColor(theme, 'success') }}
          />
          <Chip
            size="small"
            icon={<CancelOutlinedIcon sx={{ fontSize: '14px !important' }} />}
            label="分歧"
            sx={{ height: 24, bgcolor: 'rgba(198,40,40,0.08)', color: statusTextColor(theme, 'error') }}
          />
        </Stack>
      </Stack>

      {isMobileLayout ? (
        <ConsensusComparisonMobileList
          clusters={clusters}
          participantColumns={participantColumns}
          participantView={participantView}
          currentUserId={currentUserId}
          officialUserIds={officialUserIds}
          officialColumns={officialColumns}
          videoId={videoId}
          onJumpToTime={onJumpToTime}
          onPlayCluster={openVideoPreview}
          canEditAssertion={canEditAssertion}
          onEdit={openEdit}
        />
      ) : (
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: { xs: 360, sm: 480, md: 560 },
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: needsHorizontalScroll ? `${(colCount / MAX_VISIBLE_COLS) * 100}%` : '100%',
            minWidth: needsHorizontalScroll ? tableMinWidth : colCount * 120,
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: headBg,
            },
            '& .MuiTableCell-stickyHeader:not(:first-of-type)': {
              zIndex: Z_STICKY_HEADER,
            },
            '& .MuiTableCell-stickyHeader:first-of-type': {
              zIndex: Z_STICKY_HEADER_CORNER,
              left: 0,
            },
          }}
        >
          <TableHead
            sx={{
              '& th': {
                boxShadow: (t) =>
                  `inset 0 -2px 0 ${t.palette.divider}, 0 1px 0 ${t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
              },
            }}
          >
            <TableRow
              sx={{
                '& th:not(:first-of-type)': {
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.25,
                  fontWeight: 700,
                  ...dataColWidthSx,
                  ...stickyHeaderCellSx,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& th:first-of-type': {
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.25,
                  fontWeight: 700,
                  ...firstColWidthSx,
                  ...stickyFirstHeaderSx,
                  overflow: 'visible',
                  textOverflow: 'clip',
                },
              }}
            >
              <TableCell>
                <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                  時間 / 場上判決
                </Typography>
              </TableCell>
              {officialColumns.map((col) => (
                <TableCell
                  key={col.userId || 'official-default'}
                  sx={{
                    bgcolor: officialHeadBg,
                    backgroundColor: officialHeadBg,
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main', flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight={700} noWrap title={col.label}>
                      {col.label}
                    </Typography>
                  </Stack>
                </TableCell>
              ))}
              {participantColumns.map((col) => (
                <TableCell key={col.userId}>
                  <Typography variant="caption" fontWeight={700} noWrap title={col.label}>
                    {col.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {clusters.map((cluster) => {
              const officialAssertions = getClusterOfficialAssertions(cluster, officialUserIds);
              const ownerAssertion = getClusterOwnerAssertion(cluster, officialUserIds);
              const hasOfficialAnswer = clusterHasOfficialAnswer(cluster, officialUserIds);
              const onFieldLabel = eventOnFieldLabel(cluster, ownerAssertion);
              const consensusRate = cluster.agreementRate;
              const myAligned =
                participantView && currentUserId != null
                  ? isParticipantAlignedWithOwner(cluster, currentUserId, officialUserIds)
                  : null;
              const rowRate = participantView
                ? myAligned == null
                  ? 0
                  : myAligned
                    ? 100
                    : 0
                : consensusRate ?? 0;
              const rowTone = agreementMetricColor(rowRate, theme.palette.mode === 'dark');

              return (
                <TableRow
                  key={cluster.clusterId}
                  hover
                  sx={{
                    '& td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.25,
                      verticalAlign: 'middle',
                      ...dataColWidthSx,
                      overflow: 'hidden',
                    },
                    '& td:first-of-type': {
                      overflow: 'visible',
                      verticalAlign: 'top',
                    },
                    '&:hover td:first-of-type': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <TableCell sx={{ ...stickyFirstColSx, ...firstColWidthSx }}>
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          fontFamily="monospace"
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {formatVideoTime(cluster.anchorTimestamp)}
                        </Typography>
                        {(videoId || onJumpToTime) && (
                          <Tooltip title={videoId ? '播放影片片段' : '跳到影片'}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => openVideoPreview(cluster, onFieldLabel)}
                              sx={{ p: 0.25, flexShrink: 0 }}
                            >
                              <PlayArrowIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Chip
                          size="small"
                          label={
                            participantView
                              ? myAligned == null
                                ? '未標記'
                                : myAligned
                                  ? '一致'
                                  : '分歧'
                              : formatClusterAgreementDisplayLabel(cluster, officialUserIds)
                          }
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: rowTone.bg,
                            color: rowTone.color,
                            border: '1px solid',
                            borderColor: rowTone.border,
                          }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}
                      >
                        {onFieldLabel}
                      </Typography>
                    </Stack>
                  </TableCell>
                  {officialColumns.map((col, colIndex) => {
                    const assertion = col.userId
                      ? getClusterAssertionForUser(cluster, col.userId)
                      : ownerAssertion;
                    const showVerified = colIndex === 0;
                    return (
                      <TableCell
                        key={col.userId || 'official-default'}
                        sx={{ bgcolor: 'rgba(139,195,74,0.04)', ...cellHighlightSx(null, true) }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          {showVerified &&
                            (hasOfficialAnswer ? (
                              <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main', mt: 0.25, flexShrink: 0 }} />
                            ) : (
                              <GroupsIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                            ))}
                          <ConsensusOpinionCell
                            assertion={assertion}
                            ownerAssertion={assertion}
                            highlightReviewedDiff={false}
                            canEdit={canEditAssertion(assertion)}
                            onEdit={openEdit}
                          />
                        </Stack>
                      </TableCell>
                    );
                  })}
                  {participantColumns.map((col) => {
                    const assertion = findParticipantAssertion(cluster, col.userId);
                    const aligned =
                      assertion && officialAssertions.length
                        ? isAssertionAlignedWithOfficials(assertion, officialAssertions)
                        : null;
                    return (
                      <TableCell key={col.userId} sx={cellHighlightSx(aligned, false)}>
                        <ConsensusOpinionCell
                          assertion={assertion}
                          ownerAssertions={officialAssertions}
                          highlightReviewedDiff={hasOfficialAnswer}
                          canEdit={canEditAssertion(assertion)}
                          onEdit={openEdit}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Stack>
  );
}
